// Butcher-shop cut data. Each animal has 5-6 cuts. Each cut carries:
//   - displayName: what it shows in the kid UI
//   - alternateName: the "wow" name (guanciale, ribeye, pancetta) —
//     the secret fact that lets a kid surprise grown-ups
//   - funFact: 1-2 sentences of why this cut is special
//   - color: pastel hex for the diagram region
//   - howToMake: the technique combo the kid can try in Craft to
//     mint this cut into their inventory (connects edu → gameplay)
//
// EMOJI-IN-UI EXCEPTION: kids-mode opt-in.

export const BUTCHER_CUTS = {
  pig: {
    animalEmoji: "🐖",
    animalName: "Pig",
    cuts: [
      {
        id: "jowl",
        displayName: "Cheek",
        alternateName: "Guanciale",
        emoji: "🥓",
        color: "#FFB5A7",
        funFact: "POSH NAME: Guanciale (GWAN-cha-LAY) — the Italian secret behind real spaghetti carbonara! Most grown-ups think bacon — but it's actually this cheek cut, cured for weeks with salt and pepper.",
        howToMake: { technique: "Store", emojis: ["🐖", "🧂", "🫙"] },
      },
      {
        id: "shoulder",
        displayName: "Shoulder",
        alternateName: "Boston Butt",
        emoji: "🥩",
        color: "#FCD5CE",
        funFact: "Confusingly called 'Boston Butt' even though it's the SHOULDER. Slow-cook it for hours and the meat falls apart — that's how you make Pulled Pork.",
        howToMake: { technique: "Heat + Rest", emojis: ["🐖", "🔥", "⏳"] },
      },
      {
        id: "loin",
        displayName: "Loin",
        alternateName: "Pork Chop",
        emoji: "🥩",
        color: "#F8EDEB",
        funFact: "The strip along the pig's back. Cut crosswise = Pork Chops. Whole = a fancy Pork Roast for Sunday dinner.",
        howToMake: { technique: "Grill", emojis: ["🐖", "🍖"] },
      },
      {
        id: "belly",
        displayName: "Belly",
        alternateName: "Bacon / Pancetta",
        emoji: "🥓",
        color: "#FFC8A2",
        funFact: "The most famous cut on Earth. Smoked + sliced = Bacon. Cured Italian-style (no smoke) = Pancetta. The same belly, two countries, two breakfasts.",
        howToMake: { technique: "Heat", emojis: ["🐖", "🔥"] },
      },
      {
        id: "ham",
        displayName: "Ham",
        alternateName: "Prosciutto",
        emoji: "🍖",
        color: "#F4A6A0",
        funFact: "The back leg. Cooked = the Ham at Christmas. Air-dried for 18 months in the Italian Alps = Prosciutto, the fancy paper-thin slices.",
        howToMake: { technique: "Bake", emojis: ["🐖", "♨️"] },
      },
      {
        id: "ribs",
        displayName: "Ribs",
        alternateName: "Spare Ribs",
        emoji: "🍖",
        color: "#FAD2C9",
        funFact: "Long bones with meat between them. Slow-cooked with BBQ sauce until the meat falls off the bone. The #1 reason kids ask for seconds.",
        howToMake: { technique: "Heat + Rest", emojis: ["🐖", "🔥", "⏳"] },
      },
    ],
  },
  cow: {
    animalEmoji: "🐄",
    animalName: "Cow",
    cuts: [
      {
        id: "chuck",
        displayName: "Chuck",
        alternateName: "Pot Roast",
        emoji: "🥩",
        color: "#D4A574",
        funFact: "The shoulder. Tougher muscle that needs LOW and SLOW cooking — perfect for pot roasts and ground beef for hamburgers.",
        howToMake: { technique: "Blend", emojis: ["🐄", "🌀"] },
      },
      {
        id: "rib",
        displayName: "Rib",
        alternateName: "Ribeye Steak",
        emoji: "🥩",
        color: "#C77B5C",
        funFact: "The fanciest steak in the world. Marbled with fat that melts when grilled — that's why a Ribeye costs the most at the steakhouse.",
        howToMake: { technique: "Grill", emojis: ["🐄", "🍖"] },
      },
      {
        id: "loin",
        displayName: "Loin",
        alternateName: "Filet Mignon",
        emoji: "🥩",
        color: "#E8C4A0",
        funFact: "POSH NAME: Filet Mignon (fee-LAY meen-YON) — French for 'dainty fillet.' The tenderest piece on the whole cow. You can almost cut it with a fork.",
        howToMake: { technique: "Pan-Fry", emojis: ["🐄", "🍳"] },
      },
      {
        id: "brisket",
        displayName: "Brisket",
        alternateName: "Texas BBQ",
        emoji: "🥩",
        color: "#A0522D",
        funFact: "The chest. Texas BBQ pit-masters cook it for 12+ hours with smoke until it's so tender you don't need a knife. Worth waiting for.",
        howToMake: { technique: "Heat + Rest", emojis: ["🐄", "🔥", "⏳"] },
      },
      {
        id: "round",
        displayName: "Round",
        alternateName: "Roast Beef",
        emoji: "🥩",
        color: "#DEB887",
        funFact: "The back leg. Lean and big — perfect for slicing thin into Roast Beef sandwiches. The deli counter star.",
        howToMake: { technique: "Bake", emojis: ["🐄", "♨️"] },
      },
      {
        id: "flank",
        displayName: "Flank",
        alternateName: "Fajita Meat",
        emoji: "🥩",
        color: "#BC8F8F",
        funFact: "A long flat muscle along the cow's side. Marinated and grilled fast = the meat in fajitas and Korean bulgogi.",
        howToMake: { technique: "Grill", emojis: ["🐄", "🍖"] },
      },
    ],
  },
  hen: {
    animalEmoji: "🐔",
    animalName: "Chicken",
    cuts: [
      {
        id: "breast",
        displayName: "Breast",
        alternateName: "White Meat",
        emoji: "🍗",
        color: "#FFF5E1",
        funFact: "The big white-meat muscle on the front. Lean and quick to cook — chicken nuggets, chicken sandwich, chicken parm.",
        howToMake: { technique: "Pan-Fry", emojis: ["🐔", "🍳"] },
      },
      {
        id: "thigh",
        displayName: "Thigh",
        alternateName: "Dark Meat",
        emoji: "🍗",
        color: "#D4A574",
        funFact: "Most chefs prefer thighs to breasts — more flavor, less dry. The dark meat secret pros know but home cooks miss.",
        howToMake: { technique: "Bake", emojis: ["🐔", "♨️"] },
      },
      {
        id: "wing",
        displayName: "Wing",
        alternateName: "Buffalo Wing",
        emoji: "🍗",
        color: "#F4A460",
        funFact: "Invented in 1964 in Buffalo, New York. Tossed in hot sauce + butter = Buffalo Wings. The Super Bowl food.",
        howToMake: { technique: "Pan-Fry", emojis: ["🐔", "🍳"] },
      },
      {
        id: "drumstick",
        displayName: "Drumstick",
        alternateName: "Leg",
        emoji: "🍗",
        color: "#DEB887",
        funFact: "Looks like a tiny drumstick — that's why it's called one. Kids love them because they come with a built-in handle.",
        howToMake: { technique: "Bake", emojis: ["🐔", "♨️"] },
      },
      {
        id: "whole",
        displayName: "Whole Bird",
        alternateName: "Roast Chicken",
        emoji: "🍗",
        color: "#C8B89A",
        funFact: "Cook the whole bird at once with butter, salt, lemon. The roast that French chefs say defines a real cook.",
        howToMake: { technique: "Bake", emojis: ["🐔", "♨️"] },
      },
    ],
  },
  sheep: {
    animalEmoji: "🐑",
    animalName: "Sheep",
    cuts: [
      {
        id: "rack",
        displayName: "Rack",
        alternateName: "Lamb Chops",
        emoji: "🥩",
        color: "#E8C4A0",
        funFact: "A row of 8 little ribs. Sliced apart = Lamb Chops. The fanciest cut on the sheep — Sunday-dinner stuff.",
        howToMake: { technique: "Grill", emojis: ["🐑", "🍖"] },
      },
      {
        id: "shoulder",
        displayName: "Shoulder",
        alternateName: "Slow Lamb",
        emoji: "🥩",
        color: "#D2B48C",
        funFact: "Tough muscle that needs hours of slow cooking. Mediterranean grandmothers braise it with garlic and rosemary until it falls apart.",
        howToMake: { technique: "Heat + Rest", emojis: ["🐑", "🔥", "⏳"] },
      },
      {
        id: "leg",
        displayName: "Leg",
        alternateName: "Easter Roast",
        emoji: "🍖",
        color: "#BC8F8F",
        funFact: "The traditional Easter dinner. A whole leg roasted with herbs — one leg feeds a big family.",
        howToMake: { technique: "Bake", emojis: ["🐑", "♨️"] },
      },
      {
        id: "loin",
        displayName: "Loin",
        alternateName: "Lamb Loin",
        emoji: "🥩",
        color: "#F5DEB3",
        funFact: "The back. Smaller and more tender than the leg. Pan-seared = a five-minute fancy dinner.",
        howToMake: { technique: "Pan-Fry", emojis: ["🐑", "🍳"] },
      },
    ],
  },
};

export function butcherFor(animalKey) {
  return BUTCHER_CUTS[animalKey] || null;
}

export const BUTCHER_ANIMALS = Object.keys(BUTCHER_CUTS);
